import type { FormField, DropdownData, ReactiveFormValue } from '@fitogest/reactive-form-builder/interfaces';
import { EventPhase, FormPhase, ReactiveFormRequestCoordinator } from '@fitogest/reactive-form-builder/form-request';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { VisitasVariedadMesModel } from 'src/app/models/visitas_variedad_mes/visitas_variedad_mes.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';
import type { ParcelasDropdownContextFactory } from './parcelas-dropdown-context.factory';
import {
    ParcelasVariedadVariosAdapter,
    type ParcelasVariedadVariosCreateRequest,
    type ParcelasVariedadVariosResult,
} from './parcelas-variedad-varios.adapter';

export type ParcelasFormRequestLifecycleContext = {
    readonly formRequest: ReactiveFormRequestCoordinator<ParcelasModel>;
    readonly env: ParcelasEnvironmentDefinition;
    readonly formFieldsDatosParcela: readonly FormField<object>[];
    readonly variedadesDropdown: DropdownData<VisitasVariedadMesModel>;
    readonly createVariedad: (
        request: ParcelasVariedadVariosCreateRequest
    ) => Promise<readonly Partial<VisitasVariedadMesModel>[] | null | undefined>;
    readonly createDropdownContextFactory: () => ParcelasDropdownContextFactory;
    readonly applyLegacyModelPatch: (patch: Partial<ParcelasModel>) => void;
    readonly markVariedadVariosCreated: () => void;
    readonly applyFormModelPatch: (patch: Partial<ReactiveFormValue<ParcelasModel>>) => void;
    readonly logError?: (message: string, error: unknown) => void;
};

const SEND_BEFORE_ERROR_PREFIX = 'catch en getPosition: ';

/**
 * Registers and executes request lifecycle hooks owned by Parcelas.
 *
 * This keeps SEND BEFORE orchestration out of the Angular component while preserving the existing form request flow.
 */
export class ParcelasFormRequestLifecycleAdapter {

    public static registerSendBefore(context: ParcelasFormRequestLifecycleContext): void {
        context.formRequest.addEvent(FormPhase.SEND, EventPhase.BEFORE, (resolve) => {
            ParcelasFormRequestLifecycleAdapter.executeSendBefore(context)
                .then(() => {
                    resolve(true);
                    return;
                })
                .catch((error: unknown) => {
                    context.logError?.(SEND_BEFORE_ERROR_PREFIX + String(error), error);
                });
        });
    }

    public static async executeSendBefore(context: ParcelasFormRequestLifecycleContext): Promise<void> {
        const dropdownContextFactory = context.createDropdownContextFactory();
        const result = await ParcelasVariedadVariosAdapter.ensureVariosSelection({
            env: context.env,
            formFieldsDatosParcela: context.formFieldsDatosParcela,
            variedadesDropdown: context.variedadesDropdown,
            createVariedad: context.createVariedad,
            resolveCurrentFieldValue: (fieldName) => dropdownContextFactory.resolveCurrentFieldValue(fieldName),
            resolveCurrentDropdownRawValue: (fieldName, value, selected) => dropdownContextFactory.resolveCurrentDropdownRawValue(
                fieldName,
                value,
                selected,
            ),
        });

        ParcelasFormRequestLifecycleAdapter.applyVariosResult(context, result);
    }

    private static applyVariosResult(
        context: ParcelasFormRequestLifecycleContext,
        result: ParcelasVariedadVariosResult,
    ): void {
        if (result.legacyModelPatch?.id_variedad !== undefined) {
            context.applyLegacyModelPatch({
                id_variedad: result.legacyModelPatch.id_variedad,
            });
        }

        if (result.created) {
            context.markVariedadVariosCreated();
        }

        if (result.shouldUpdateForm && result.formModelPatch) {
            context.applyFormModelPatch(result.formModelPatch);
        }
    }
}
